import moment from "moment";
import React, { FC, useState } from "react";

import {
  modalCancel,
  modalInput,
  modalPrimary,
  PortalModal,
} from "../../clientPortal/PortalModal";
import { DraftSigner } from "../signature.types";

export type SendSettings = {
  subject?: string;
  message?: string;
  signBy?: string | null;
  reminderDays: number;
  inOrder: boolean;
};

const label = "flex flex-col gap-1.5 text-[13px] font-bold text-[#3A4256]";

/** Step 2: the email the signers get, the deadline, reminders and signing order. */
export const SendSignatureModal: FC<{
  signers: DraftSigner[];
  clientName: string;
  documentName: string;
  busy: boolean;
  onClose: () => void;
  onSend: (settings: SendSettings) => Promise<string | null>;
}> = ({ signers, clientName, documentName, busy, onClose, onSend }) => {
  const [subject, setSubject] = useState(
    documentName
      ? `Please sign: ${documentName.replace(/\.pdf$/i, "")}`
      : `Please sign: documents for ${clientName || "you"}`,
  );
  const [message, setMessage] = useState("");
  const [signBy, setSignBy] = useState(
    moment().add(14, "days").format("YYYY-MM-DD"),
  );
  const [reminderDays, setReminderDays] = useState(3);
  const [inOrder, setInOrder] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setError(null);
    if (signBy && moment(signBy).isBefore(moment(), "day"))
      return setError("The “sign by” date is in the past.");
    const problem = await onSend({
      subject: subject.trim() || undefined,
      message: message.trim() || undefined,
      // End of the chosen day in the sender's time zone.
      signBy: signBy ? moment(signBy).endOf("day").toISOString() : null,
      reminderDays,
      inOrder,
    });
    if (problem) setError(problem);
  };

  return (
    <PortalModal
      title={"Send for signature"}
      subtitle={"Step 2 of 2 · Each signer gets an email with a personal link."}
      onClose={onClose}
      width={560}
      footer={
        <>
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Back
          </button>
          <button
            type={"button"}
            className={modalPrimary}
            onClick={send}
            disabled={busy}>
            {busy ? "Sending…" : "Send"}
          </button>
        </>
      }>
      <div className={"flex flex-wrap gap-1.5"}>
        {signers.map((s, i) => (
          <span
            key={s.key}
            className={
              "flex items-center gap-1.5 rounded-full bg-[#F4F5F8] px-2.5 py-1 text-[12px] font-bold text-[#3A4256]"
            }>
            <span
              className={"h-2 w-2 rounded-full"}
              style={{ background: s.color }}
            />
            {inOrder ? `${i + 1}. ` : ""}
            {s.kind === "firm" ? "You" : s.name || s.email}
          </span>
        ))}
      </div>
      <label className={label}>
        Subject
        <input
          className={modalInput}
          value={subject}
          maxLength={200}
          onChange={(e) => setSubject(e.target.value)}
        />
      </label>
      <label className={label}>
        Message (optional)
        <textarea
          className={`${modalInput} h-24 py-2`}
          value={message}
          maxLength={2000}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={
            "Hi, please review and sign the attached engagement letter. Thank you!"
          }
        />
      </label>
      <div className={"grid grid-cols-1 gap-3 sm:grid-cols-2"}>
        <label className={label}>
          Sign by
          <input
            type={"date"}
            className={modalInput}
            value={signBy}
            min={moment().format("YYYY-MM-DD")}
            onChange={(e) => setSignBy(e.target.value)}
          />
        </label>
        <label className={label}>
          Reminders
          <select
            className={modalInput}
            value={reminderDays}
            onChange={(e) => setReminderDays(Number(e.target.value))}>
            <option value={3}>Every 3 days</option>
            <option value={7}>Every week</option>
            <option value={0}>Off</option>
          </select>
        </label>
      </div>
      {signers.length > 1 && (
        <label
          className={
            "flex items-center gap-2 text-[13px] font-semibold text-[#3A4256]"
          }>
          <input
            type={"checkbox"}
            checked={inOrder}
            onChange={(e) => setInOrder(e.target.checked)}
            className={"accent-[#6B4EFF]"}
          />
          Sign in order (each person is emailed after the one before signs)
        </label>
      )}
      <span
        className={
          "rounded-[10px] bg-[#F7F5FF] px-3 py-2.5 text-[12.5px] font-medium text-[#3A4256]"
        }>
        Clients confirm who they are with a one-time code sent to their email
        before signing. The signed PDF and its certificate are saved to the
        client’s Files and shared in their portal.
      </span>
      {error && (
        <div className={"text-[13px] font-semibold text-[#B42318]"}>
          {error}
        </div>
      )}
    </PortalModal>
  );
};
